// src/app/pages/t0-dashboard/components/t0-stats/t0-stats.component.ts
import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, OnChanges, SimpleChanges, Input, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { AnalysisStore, AnalysisRun } from '../../../core/shared/t0-model/analysis.store';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { T0ApiResponse, Year } from '../../../core/shared/t0-model/t0-response-model';

type ViewCard = {
  id: 'total_ht' | 'undiagnosed' | 'diagnosed' | 'treated' | 'controlled';
  title: string;
  value: number;
  percent: number;
  barPercent: number;
  tooltip?: string | null;
};

type HorizonYears = 5 | 10 | 15 | 20;

@Component({
  selector: 'app-t0-stats',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  templateUrl: './t0-stats.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class T0StatsComponent implements OnInit, OnDestroy, OnChanges {
  private store = inject(AnalysisStore);
  private destroyed$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);

  @Input() tooltipTotalOverride: string | null = null;
  @Input() enableTooltip = true;
  @Input() horizon: HorizonYears = 5;

  selectedSimulationTitle = '-';
  cards: ViewCard[] = [];

  private currentRun?: AnalysisRun;

  ngOnInit(): void {
    combineLatest([this.store.runsList$, this.store.selectedRunId$])
      .pipe(
        map(([runs, selId]) => {
          const sel = selId ? runs.find((r) => r.id === selId) : (runs.length ? runs[runs.length - 1] : undefined);
          return { run: sel };
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ run }) => {
        this.currentRun = run;
        this.selectedSimulationTitle = run?.title ?? '-';
        this.cards = this.buildCards(run);
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['horizon'] && this.currentRun) {
      this.cards = this.buildCards(this.currentRun);
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get tooltipTotal(): string | null {
    if (!this.enableTooltip) return null;
    if (this.tooltipTotalOverride && this.tooltipTotalOverride.trim().length) {
      return this.tooltipTotalOverride.trim();
    }
    return this.cards.length ? this.cards[0].tooltip ?? null : null;
  }

  getCard(i: number): ViewCard | null {
    return this.cards[i] ?? null;
  }

  private buildCards(run?: AnalysisRun): ViewCard[] {
    if (!run?.t0) return [];

    const t0 = run.t0;
    const year = this.pickHorizonYearOrLatest(t0, this.horizon);

    const diagnosed = this.round0(this.store.pick(t0, 'Diagnosed', year));
    const undiagnosed = this.round0(this.store.pick(t0, 'Undiagnosed', year));
    const treated = this.round0(this.store.pick(t0, 'Treated', year));
    const controlled = this.round0(this.store.pick(t0, 'Controlled', year));

    const totalHt = this.round0(diagnosed + undiagnosed);

    const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

    const undiagnosedPct = pct(undiagnosed, totalHt);
    const diagnosedPct = pct(diagnosed, totalHt);
    const treatedPct = pct(treated, diagnosed);
    const controlledPct = pct(controlled, diagnosed);

    const totalTooltip = totalHt > 0 ? `Total HT population: ${this.formatNumber(totalHt)}.` : null;

    return [
      this.card('total_ht', 'Total HT Population', totalHt, 100, totalTooltip),
      this.card('undiagnosed', 'Undiagnosed', undiagnosed, undiagnosedPct),
      this.card('diagnosed', 'Diagnosed', diagnosed, diagnosedPct),
      this.card('treated', 'Treated', treated, treatedPct),
      this.card('controlled', 'Controlled', controlled, controlledPct),
    ];
  }

  private round0(n: any): number {
    const v = Number(n);
    return Number.isFinite(v) ? Math.round(v) : 0;
  }

private pickHorizonYearOrLatest(t0: T0ApiResponse, horizon: HorizonYears): Year | null {
  const targetYear =
    horizon === 5 ? 2031 :
    horizon === 10 ? 2036 :
    horizon === 15 ? 2041 :
    2046;

  const years = Array.isArray(t0?.years) ? t0.years.map(Number) : [];
  if (!years.length) return null;

  if (years.includes(targetYear)) return targetYear as Year;
  return years[years.length - 1] as Year;
}

  private card(id: ViewCard['id'], title: string, value: number, percent: number, tooltip?: string | null): ViewCard {
    const barPercent = Math.max(0, Math.min(100, percent));
    return { id, title, value, percent, barPercent, tooltip: tooltip ?? null };
  }

  formatNumber(n: number | null | undefined): string {
    if (n === null || n === undefined) return '';
    return new Intl.NumberFormat('en-US').format(Math.round(Number(n)));
  }
}